import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PageData, PageEvent } from '@nx-vigil/core/web/shared/util';
import { ButtonModule } from '@openng/optimus-ui/button';
import {
  PrimeTableComponent,
  type PrimeTableRowToggleEvent,
} from './prime-table.component';
import {
  defaultTableOption,
  TableColumnOption,
  TableOption,
  TableSortEvent,
} from './table';

@Component({
  selector: 'core-prime-loadable-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonModule, MatProgressSpinnerModule, PrimeTableComponent],
  template: `<div>
    <core-prime-table
      [data]="data()"
      [tableOption]="tableOption()"
      [columns]="columns()"
      (cellClick)="cellClick.emit($event)"
      (rowToggle)="rowToggle.emit($event)"
      (sortChange)="sortChange.emit($event)"
    ></core-prime-table>
    @if (isLoading() || hasMore()) {
      <div class="flex justify-center my-3">
        <p-button
          severity="secondary"
          data-testid="btnLoadMore"
          iconPos="right"
          (click)="onLoadMore()"
          [disabled]="isLoading() || !hasMore()"
          [loading]="isLoading()"
          [label]="isLoading() ? loadingMoreLabel : loadMoreLabel"
        >
        </p-button>
      </div>
    }
  </div>`,
  styles: [``],
})
export class PrimeLoadableTableComponent<T extends object> {
  protected readonly loadingMoreLabel = $localize`:@@loading-more:Loading...`;
  protected readonly loadMoreLabel = $localize`:@@load-more:Load More...`;
  public readonly isLoading = input(false);
  public readonly tableOption = input<TableOption<T>>(defaultTableOption);
  public readonly columns = input<TableColumnOption<T>[]>([]);
  public readonly pageData = input.required<PageData<T>>();
  public readonly loadMore = output<PageEvent>();
  public readonly dataChanged = output<readonly T[]>();
  public readonly cellClick = output<{
    row: T;
    column: TableColumnOption<T>;
    event: MouseEvent;
  }>();
  public readonly rowToggle = output<PrimeTableRowToggleEvent<T>>();
  public readonly sortChange = output<TableSortEvent>();
  private readonly pageSize = computed(
    () => this.tableOption().pageSize ?? <number>defaultTableOption.pageSize,
  );
  private readonly pageIndex = computed(() => this.pageData().event.pageIndex);
  private readonly isFirstPage = computed(() => this.pageIndex() === 0);
  private readonly totalCount = computed(() => this.pageData().event.length);
  protected readonly hasMore = computed(() => {
    const totalPages = Math.ceil(this.totalCount() / this.pageSize());
    return this.pageIndex() + 1 < totalPages;
  });
  private readonly currentData = signal<T[]>([]);
  protected readonly data = computed(() => {
    const currentData = untracked(() => this.currentData());
    const pageData = this.pageData().data ?? [];
    const data = this.isFirstPage() ? pageData : [...currentData, ...pageData];
    if (data !== currentData) {
      untracked(() => this.currentData.set(data));
      this.dataChanged.emit(data);
    }
    return data;
  });

  protected onLoadMore(): void {
    if (this.isLoading() || !this.hasMore()) {
      return;
    }
    this.loadMore.emit({
      pageIndex: this.pageIndex() + 1,
      pageSize: this.pageSize(),
      length: this.totalCount(),
    });
  }
}
